/**
 * The icon set.
 *
 * Every Studio42 application starts with this. Add glyphs an application needs;
 * keep the grid, the stroke weight and `currentColor`.
 *
 * ── Why not emoji ────────────────────────────────────────────────────────────
 *
 * Emoji were quicker and they were the wrong call. They render as a different
 * picture on every platform — a car emoji is a red saloon on Apple, a blue hatchback on
 * Android, and a wireframe on some Windows builds — so a legend that means
 * "this vessel takes cars" is a different drawing to every second person
 * reading it. They cannot inherit `currentColor`, so they ignore the theme and
 * sit at the wrong weight beside real text. Their baseline and advance width
 * are inconsistent, which is exactly what makes a column of them fail to line
 * up. And a screen reader announces the Unicode name, not the meaning.
 *
 * These are one stroke weight, one grid, and `currentColor` throughout, so an
 * icon beside a label is the same colour and weight as the label.
 *
 * STUDIO42 RULE: icons, never emoji, in any application UI. Emoji remain fine
 * in commit messages and documentation.
 */

export type IconName =
  | 'ferry' | 'ship' | 'cargo' | 'car' | 'bicycle' | 'pet' | 'luggage'
  | 'seat' | 'door' | 'accessible' | 'people' | 'person'
  | 'calendar' | 'clock' | 'route' | 'ticket' | 'scan'
  | 'check' | 'alert' | 'info' | 'chevron' | 'back' | 'plus' | 'close'
  | 'heart' | 'swap' | 'search' | 'pin' | 'box' | 'chat' | 'leaf' | 'sun' | 'map' | 'share'
  | 'chart' | 'settings' | 'key' | 'shield' | 'globe' | 'home'
  | 'tag' | 'gift' | 'handshake' | 'image' | 'arrow'
  | 'bell' | 'camera' | 'pencil' | 'wrench' | 'hammer' | 'plug' | 'phone' | 'utensils' | 'shirt' | 'blocks' | 'book' | 'star' | 'coconut' | 'block' | 'download' | 'trash' | 'coqui' | 'parrot' | 'palm'
  | 'eye' | 'rocket' | 'rotate-left' | 'rotate-right' | 'crop' | 'flip' | 'sparkles'

/** 24×24, 1.75 stroke, round caps. One grid so a row of them lines up. */
const PATHS: Record<IconName, string> = {
  heart: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.9-8.6a5.5 5.5 0 0 0-.1-7.8',
  swap: 'M3 7h17M15 2l5 5-5 5M21 17H4M9 12l-5 5 5 5',
  search: 'M21 21l-5-5M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14',
  pin: 'M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0zM12 7v6M9 10h6',
  box: 'M3 7l9-5 9 5v10l-9 5-9-5zM3 7l9 5 9-5M12 12v10',
  chat: 'M3 4h18v13H8l-5 4zM7 8h10M7 12h6',
  leaf: 'M20 3C6 2 3 8 5 14s9 8 13-1c2-4 2-7 2-10M3 22 16 9',
  sun: 'M12 4V2M12 22v-2M4 12H2M22 12h-2M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10',
  // A hull and a wheelhouse. Reads as a boat at 16px, which is the only size
  // that matters here.
  ferry: 'M3 17.5c1.6 0 1.6 1.2 3.2 1.2s1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2 1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2M4.5 14.5 6 9.5h12l1.5 5M8 9.5V6h5l2 3.5M11 6V3.5',
  ship: 'M3 17.5c1.6 0 1.6 1.2 3.2 1.2s1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2 1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2M5 14.5 6.5 10h11L19 14.5M12 10V4M12 4l4 2-4 2',
  cargo: 'M3 8.5h18v10H3zM3 8.5 6 4h12l3 4.5M12 4v4.5M8 12.5h2M14 12.5h2',
  car: 'M4 16.5v2.2M20 16.5v2.2M3 16.5v-4l1.8-4.4A1.6 1.6 0 0 1 6.3 7h11.4a1.6 1.6 0 0 1 1.5 1.1L21 12.5v4zM3 12.5h18M7 14.7h.01M17 14.7h.01',
  bicycle: 'M6.5 18.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M17.5 18.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M6.5 15h5l3.5-7M9.5 8h4M14 8l3.5 7',
  pet: 'M12 20c-2.8 0-4.6-1.7-4.6-3.6 0-1.8 1.7-2.6 2.7-4 .8-1 1-2 1.9-2s1.1 1 1.9 2c1 1.4 2.7 2.2 2.7 4 0 1.9-1.8 3.6-4.6 3.6M6 10.5a1.6 2 30 1 0 0-4 1.6 2 30 1 0 0 4M18 10.5a1.6 2 -30 1 0 0-4 1.6 2 -30 1 0 0 4',
  luggage: 'M5.5 8.5h13v11h-13zM9 8.5V5.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3M8 19.5v1.5M16 19.5v1.5M12 11.5v5',
  seat: 'M7 5.5h2.5a2 2 0 0 1 2 1.8l.6 5.7H7zM5 14.5h14a1.5 1.5 0 0 1 0 3h-1.6l-.8 3H7.4l-.8-3H5a1.5 1.5 0 0 1 0-3M12 13V6.5a1 1 0 0 1 1-1h4',
  // A doorway with the leaf swung open — "by the door", not "an exit sign".
  door: 'M4 20.5h16M7 20.5V5a1 1 0 0 1 1.2-1l7 -1.4A1 1 0 0 1 16.5 3.6v16.9M7 20.5h9.5M13.8 12.2h.01',
  accessible: 'M12 5.2a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8M10 8.2l3.4-.9v3.6l3.1 1.4M10 8.2v4.4h3.4M9.6 12.9a4.2 4.2 0 1 0 5 5.6M13.5 12.6l2 5.9h2.2',
  people: 'M9 11.5a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M2.8 19.8v-.9A4.6 4.6 0 0 1 7.4 14.3h3.2a4.6 4.6 0 0 1 4.6 4.6v.9M16.2 5.4a3.2 3.2 0 0 1 0 6.2M17.6 14.4h.6a4.6 4.6 0 0 1 4.6 4.5v.9',
  person: 'M12 11.8a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2M4.8 20.2v-1a4.8 4.8 0 0 1 4.8-4.8h4.8a4.8 4.8 0 0 1 4.8 4.8v1',
  calendar: 'M4.5 6.5h15v14h-15zM4.5 10.5h15M8.5 3.5v4M15.5 3.5v4M8.5 14h.01M12 14h.01M15.5 14h.01M8.5 17.2h.01M12 17.2h.01',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 7v5.2l3.4 2',
  route: 'M6 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M18 21.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M6 7.5v3.2a3 3 0 0 0 3 3h6a3 3 0 0 1 3 3v.8',
  ticket: 'M3.5 8.2V6.5h17v1.7a2.3 2.3 0 0 0 0 4.6v1.7h-17v-1.7a2.3 2.3 0 0 0 0-4.6M9.5 6.5v8M9.5 8.8v.01M9.5 11.6v.01',
  scan: 'M3.5 8V5a1.5 1.5 0 0 1 1.5-1.5h3M16 3.5h3A1.5 1.5 0 0 1 20.5 5v3M20.5 16v3a1.5 1.5 0 0 1-1.5 1.5h-3M8 20.5H5A1.5 1.5 0 0 1 3.5 19v-3M3.5 12h17',
  check: 'M4.5 12.8 9.5 17.8 19.5 6.8',
  alert: 'M12 8.5v5M12 17h.01M10.3 3.9 2.5 17.4a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 11v5M12 7.6h.01',
  chevron: 'M9 5.5 15.5 12 9 18.5',
  back: 'M15 5.5 8.5 12 15 18.5',
  plus: 'M12 5v14M5 12h14',
  close: 'M6 6l12 12M18 6 6 18',
  map: 'M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2zM9 4v14M15 6v14',
  share: 'M12 15V3M8 7l4-4 4 4M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  settings: 'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M19.1 14.4a1.5 1.5 0 0 0 .3 1.6l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-2.5 1.1v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-2.6-1 1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0-1.1-2.5H4.3a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1-2.6l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.6.3h.1a1.5 1.5 0 0 0 .9-1.4V4.3a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 2.5 1.1l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0 1.1 2.5h.2a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9',
  key: 'M15.5 13.5a5 5 0 1 0-4.6-3H3v3.2h2.6v2.6h2.6v-2.6h2.7a5 5 0 0 0 4.6 2.8M16.8 8.2h.01',
  eye: 'M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  rocket: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2zM9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5',
  shield: 'M12 21.5s7.5-3.4 7.5-9.2V5.9L12 2.9 4.5 5.9v6.4c0 5.8 7.5 9.2 7.5 9.2',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M3.2 9.5h17.6M3.2 14.5h17.6M12 3a13.5 13.5 0 0 1 0 18M12 3a13.5 13.5 0 0 0 0 18',
  home: 'M3.5 10.5 12 3.5l8.5 7M5.8 12.4v8.1h12.4v-8.1',
  tag: 'M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1.6 1.6 0 0 1 0 2.3l-5.4 5.4a1.6 1.6 0 0 1-2.3 0zM8 8h.01',
  gift: 'M4 11.5h16V20H4zM3 7.5h18v4H3zM12 7.5V20M12 7.5C10.5 7.5 8 7 8 5.2 8 3.8 9.2 3.2 10.2 3.7 11.4 4.3 12 7.5 12 7.5M12 7.5c1.5 0 4-.5 4-2.3 0-1.4-1.2-2-2.2-1.5C12.6 4.3 12 7.5 12 7.5',
  handshake: 'm11 17 2 2a1 1 0 1 0 3-3M14 14l2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4M21 3l1 11h-2M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3M3 4h8',
  image: 'M4 4.5h16v15H4zM4 16l4.5-4.5 3.5 3.5 3-3 5 5M9 9.2h.01',
  arrow: 'M4 12h15M13.5 6.5 19 12l-5.5 5.5',
  bell: 'M6 16.500V11a6 6 0 1 1 12 0v5.500l1.500 2H4.500zM10 21a2.200 2.200 0 0 0 4 0',
  camera: 'M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  pencil: 'M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5zM15 5l4 4',
  wrench: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z',
  hammer: 'm15 12-8.373 8.373a1 1 0 1 1-3-3L12 9M18 15l4-4M21.5 11.5l-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.202-1.756L9 2.96l.92.82A6.18 6.18 0 0 1 12 8.4V10l2 2h1.172a2 2 0 0 1 1.414.586L18.5 14.5',
  plug: 'M12 22v-5M9 8V2M15 8V2M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z',
  phone: 'M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM12 18h.01',
  utensils: 'M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7',
  shirt: 'M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z',
  blocks: 'M3 14h7v7H3zM14 14h7v7h-7zM8.5 3h7v7h-7z',
  book: 'M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20',
  coconut: 'M12 21a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM12 7c.5-2.5 2.5-4 6-4-.3 3-2.3 4.6-6 4zM9.5 14h.01M14.5 14h.01M12 17h.01',
  coqui: 'M5 15c0-3 3-5.5 7-5.5s7 2.5 7 5.5-3 4-7 4-7-1-7-4zM8 9.5a1.8 1.8 0 1 1 .01 0M16 9.5a1.8 1.8 0 1 0-.01 0M10 15.5c1.3.8 2.7.8 4 0M5 18.5 3 21M19 18.5l2 2.5',
  parrot: 'M15 4a5 5 0 0 0-5 5v2c0 3-3 4-3 8h5c3 0 5-3 5-7V9a5 5 0 0 0-2-5zM15 8h.01M9 20l-1 2M13 19l1 2M18 7l3 2-3 2',
  palm: 'M12 21V10M12 10C10 7 6 6 3 8c3 0 5 1 6.5 3M12 10c2-3 6-4 9-2-3 0-5 1-6.5 3M12 10c-1-3-1-5 0-7 1 2 1 4 0 7M10 21h4',
  block: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM5.6 5.6l12.8 12.8',
  download: 'M12 4v11M7.5 10.5 12 15l4.5-4.5M5 20h14',
  trash: 'M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 13h9l1-13',
  'rotate-left': 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  'rotate-right': 'M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5',
  crop: 'M6 2v14a2 2 0 0 0 2 2h14M18 22V8a2 2 0 0 0-2-2H2',
  flip: 'M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3M12 2v2M12 8v2M12 14v2M12 20v2',
  sparkles: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z',
  star: 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z',
}

export default function Icon({ name, size = 20, className, title }: {
  name: IconName
  size?: number
  className?: string
  title?: string
}) {
  return (
    <svg
      className={['icon', className].filter(Boolean).join(' ')}
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={1.75}
      strokeLinecap="round" strokeLinejoin="round"
      // Decorative unless it is the only thing carrying the meaning.
      role={title ? 'img' : undefined} aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title && <title>{title}</title>}
      <path d={PATHS[name]} />
    </svg>
  )
}

/**
 * Flags, for the language switch.
 *
 * Filled rather than stroked, because a flag read at 20px is a colour, not an
 * outline — and the whole reason a flag beats the word "Español" here is that it
 * is recognised before it is read.
 *
 * Puerto Rico for Spanish rather than Spain: the strings are `es-PR`, the
 * wording is Puerto Rican, and to the people using this on the island the
 * Spanish flag would be somebody else's.
 */
export function Flag({ locale, size = 20 }: { locale: 'en' | 'es-PR'; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', 'aria-hidden': true, focusable: 'false' } as const
  if (locale === 'es-PR') {
    return (
      <svg {...common} className="flag">
        <defs><clipPath id="pr-c"><circle cx="12" cy="12" r="11" /></clipPath></defs>
        <g clipPath="url(#pr-c)">
          <rect x="0" y="1" width="24" height="22" fill="#ED0000" />
          <rect x="0" y="5.4" width="24" height="4.4" fill="#fff" />
          <rect x="0" y="14.2" width="24" height="4.4" fill="#fff" />
          <path d="M0 1 13 12 0 23Z" fill="#0050F0" />
          <path d="m4.3 9.4.86 2.02 2.19.17-1.67 1.43.51 2.14-1.89-1.15-1.89 1.15.51-2.14L1.25 11.6l2.19-.17Z" fill="#fff" />
        </g>
        <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeOpacity=".18" />
      </svg>
    )
  }
  return (
    <svg {...common} className="flag">
      <defs><clipPath id="us-c"><circle cx="12" cy="12" r="11" /></clipPath></defs>
      <g clipPath="url(#us-c)">
        <rect x="0" y="1" width="24" height="22" fill="#fff" />
        {[0, 2, 4, 6, 8, 10].map((i) => (
          <rect key={i} x="0" y={1 + i * 1.7 + 1.7} width="24" height="1.7" fill="#B22234" />
        ))}
        <rect x="0" y="1" width="24" height="1.7" fill="#B22234" />
        <rect x="0" y="1" width="10.5" height="11.9" fill="#3C3B6E" />
        {[[2.4, 3.2], [5.2, 3.2], [8, 3.2], [3.8, 5.4], [6.6, 5.4], [2.4, 7.6], [5.2, 7.6], [8, 7.6], [3.8, 9.8], [6.6, 9.8]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r=".82" fill="#fff" />
        ))}
      </g>
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeOpacity=".18" />
    </svg>
  )
}
